import clsx from 'clsx'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LineArrowsRightDownIcon from 'LuxKit/Icons/line/LineArrowsRightDownIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import type SvgIcon from 'LuxKit/SvgIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import getPropertyIcon from 'lib/offer/getPropertyIcon'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import { getMaxNonInfantGuestCount } from 'lib/offer/occupancyUtils'

import { mapRoomPackage } from './roomPackageUtils'

const ROOM_SIZE_AMENITY_KEY = 'room-size'

const FlexGroup = styled(Group)`
  flex: 1;
`

const WrappableLabel = styled(StructureLabel)`
  padding-block: ${rem(2)};
`

const AmenitiesAndPoliciesCtaContainer = styled.div`
  &.start-on-new-line {
    flex-basis: 100%;
  }
`

interface Amenity {
  key: string
  name: string
  Icon: typeof SvgIcon
}

interface RoomPackageDetails {
  size?: string
  capacity?: number
  beds?: string
  bedrooms?: number
  wifi?: string
  parking?: string
  amenities?: Array<Amenity>
}

function extractPropertyRoomPackageDetailList({ capacities, bedrooms, bedGroupDescriptions, size, popularAmenities }: App.Property.Room, orWord: string): RoomPackageDetails {
  const totalCapacity = getMaxNonInfantGuestCount(capacities)

  const amenities: Array<Amenity> = []
  if (size) {
    amenities.push({ key: ROOM_SIZE_AMENITY_KEY, name: size, Icon: LineArrowsRightDownIcon })
  }
  for (const { id, name, iconName } of popularAmenities) {
    amenities.push({ key: id, name, Icon: getPropertyIcon(iconName) })
  }

  return {
    capacity: totalCapacity || undefined,
    beds: bedGroupDescriptions.join(` ${orWord} `) || undefined,
    bedrooms,
    amenities,
  }
}

function extractHotelRoomPackageDetailList(
  roomPackage: App.HotelPackage,
  orWord: string,
  showAmenities: boolean,
): RoomPackageDetails {
  const dimension = roomPackage.roomType.sizeSqm
  const beds = (roomPackage.roomType.bedGroups ?? []).map((b) => b.description).join(` ${orWord} `)
  const bedrooms = roomPackage.roomType.numberOfBedrooms
  const amenities: Array<Amenity> = []

  if (showAmenities && dimension) {
    amenities.push({ key: `${roomPackage.roomType.id}-size`, name: `${dimension}m²`, Icon: LineArrowsRightDownIcon })
  }

  if (showAmenities) {
    for (const amenity of roomPackage.roomType.amenities) {
      amenities.push({
        key: `${amenity.group}-${amenity.id}`,
        name: amenity.description,
        Icon: getOfferFeatureIcon(amenity.symbol),
      })
    }
  }

  return {
    size: showAmenities ? undefined : dimension ? `${dimension}m²` : undefined,
    beds: beds || undefined,
    bedrooms,
    amenities: amenities.length ? amenities : undefined,
  }
}

function extractBedbankRoomPackageDetailList(roomPackage: App.BedbankPackage): RoomPackageDetails {
  const features = new Map(roomPackage.features.map((f) => ([f.id, f.text])))

  return {
    beds: features.get('bed'),
    bedrooms: roomPackage.numberOfBedrooms,
    capacity: getMaxNonInfantGuestCount(roomPackage.capacities.combinations),
    size: features.get('dimension'),
    wifi: features.get('wifi'),
    parking: features.get('parking'),
  }
}

interface Props {
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  urgencyLabels?: ReactNode
  hideCounts?: boolean
  showHotelRoomAmenities?: boolean
  amenitiesAndPoliciesCta?: ReactNode
  showStandaloneAmenitiesAndPoliciesCta?: boolean
  showAmenitiesAndPoliciesCtaOnNewLine?: boolean
}

function AccommodationRoomDetailsListContent(props: Props) {
  const {
    roomPackage,
    urgencyLabels,
    hideCounts,
    showHotelRoomAmenities = false,
    amenitiesAndPoliciesCta,
    showStandaloneAmenitiesAndPoliciesCta = false,
    showAmenitiesAndPoliciesCtaOnNewLine = false,
  } = props

  const intl = useIntl()

  const orWord = intl.formatMessage({
    id: 'GzXJi62SQuqpWHf7Jmwj',
    defaultMessage: 'OR',
    description: 'Separator between alternative bed group options (e.g. "1 King Bed OR 2 Single Beds")',
  })

  const detailsList = useMemo(() => {
    return mapRoomPackage(
      roomPackage,
      (room) => extractPropertyRoomPackageDetailList(room, orWord),
      (pkg) => extractHotelRoomPackageDetailList(pkg, orWord, showHotelRoomAmenities),
      extractBedbankRoomPackageDetailList,
    )
  }, [roomPackage, orWord, showHotelRoomAmenities])

  const bedroomsLabel = (bedroomCount: number) => {
    return intl.formatMessage(
      {
        id: 'TymFMOQjKzT063HM0cSP',
        defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
        description: 'Label showing the number of bedrooms for a room/package.',
      },
      { count: bedroomCount },
    )
  }

  const sleepsLabel = (guestCount: number) => {
    return intl.formatMessage(
      {
        id: 'rHuohmpw67VoLBruN_2o',
        defaultMessage: 'Sleeps {count}',
        description: 'Label indicating the maximum guest capacity for a room/package.',
      },
      { count: guestCount },
    )
  }

  return <FlexGroup direction="vertical" horizontalAlign="start" gap={12} noDisplayWhenEmpty>
    <LabelGroup>
      {urgencyLabels}
      {detailsList.size && <StructureLabel variant="standard" startIcon={<LineArrowsRightDownIcon aria-hidden="true" />}>
        {detailsList.size}
      </StructureLabel>}
      {!hideCounts && detailsList.capacity && <StructureLabel variant="standard" startIcon={<LineUsersAltIcon aria-hidden="true" />}>
        {sleepsLabel(detailsList.capacity)}
      </StructureLabel>}
      {detailsList.beds && <WrappableLabel variant="standard" startIcon={<LineBedIcon aria-hidden="true" />}>
        {detailsList.beds}
      </WrappableLabel>}
      {!hideCounts && !!detailsList.bedrooms && <WrappableLabel variant="standard" startIcon={<LineBedIcon aria-hidden="true" />}>
        {bedroomsLabel(detailsList.bedrooms)}
      </WrappableLabel>}
    </LabelGroup>
    {(!!detailsList.amenities || (showStandaloneAmenitiesAndPoliciesCta && !!amenitiesAndPoliciesCta)) && <Group direction="horizontal" gap="8 16" wrap="wrap">
      {detailsList.amenities?.map(({ key, name, Icon }) => <BodyTextBlock key={key} variant="medium" startIcon={<Icon aria-hidden="true" />}>
        {name}
      </BodyTextBlock>)}
      {amenitiesAndPoliciesCta && <AmenitiesAndPoliciesCtaContainer
        className={clsx({ 'start-on-new-line': showAmenitiesAndPoliciesCtaOnNewLine })}
        data-testid="accommodation-room-details-cta-container"
      >
        {amenitiesAndPoliciesCta}
      </AmenitiesAndPoliciesCtaContainer>}
    </Group>}
  </FlexGroup>
}

export default AccommodationRoomDetailsListContent
